import { css } from '@emotion/react';
import { useSetAtom } from 'jotai';
import { FC, ReactNode, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { baseSampleSeeds } from '../../components/FrostedGate/sample-seeds';
import { SampleScope, seed } from '../../components/FrostedGate/SampleScope';
import { useSampleCompanies } from '../../components/FrostedGate/useSampleCompanies';
import { useSeedSampleCompanies } from '../../components/FrostedGate/useSeedSampleCompanies';
import { DealBoardDealCategory, LoadingStatus, SelectItem } from '../../types';
import { CategoryDealTable } from './components/CategoryDealTable';
import { DealAnalyticsDurationView } from './components/DealAnalytics/Details/DealAnalyticsDuration';
import { DealAnalyticsHeader } from './components/DealAnalytics/DealAnalyticsHeader';
import { BreakdownChart } from './components/DealAnalytics/Overview/BreakdownChart';
import { ConversionBarChart } from './components/DealAnalytics/Overview/ConversionBarChart';
import { OverviewChart } from './components/DealAnalytics/Overview/OverviewChart';
import { DealBoard } from './components/DealBoard/DealBoard';
import { DealFlowHeaderV2 } from './components/DealFlowHeaderV2';
import { FilterOptions } from './components/FilterOptions';
import { pipelinePreviewScreen } from './pipeline-preview-screen';
import {
  PREVIEW_CONVERSION_RATES,
  PREVIEW_DEAL_BOARD_CONFIG,
  PREVIEW_DEAL_TYPE_ID,
  PREVIEW_DEALS,
  PREVIEW_DEALS_GROUPED,
  PREVIEW_DEAL_STATS,
} from './pipeline.fixtures';
import { allDealsState, dealBoardConfigState } from './state/DealboardDataState';
import {
  DefaultDealCategory,
  selectedDealCategoryState,
  selectedDealTypeState,
} from './state/DealboardUIStateJ';

const CATEGORY_LABELS: Record<string, string> = {
  [DealBoardDealCategory.PASS]: 'Passed Deals',
  [DealBoardDealCategory.MISSED]: 'Missed Deals',
  [DealBoardDealCategory.TRACK]: 'Tracked Deals',
  [DealBoardDealCategory.CLOSED]: 'Closed Deals',
};

const boardCss = css`
  display: grid;
  grid-template-rows: auto auto 1fr;
  height: 100%;
  width: 100%;
  min-height: 0;
`;

const analyticsCss = css`
  display: grid;
  grid-template-rows: min-content 1fr;
  height: 100%;
  width: 100%;
  padding: 1rem 2.25rem;
`;

const chartsCss = css`
  display: grid;
  gap: 2rem;
`;

function categoryItem(category: string): SelectItem {
  return category === DealBoardDealCategory.CURRENT
    ? DefaultDealCategory
    : { id: category, value: CATEGORY_LABELS[category] ?? category };
}

/** The board's stores seeded with the sample: configuration, deals, the type and category the path names. */
const PipelineScope: FC<{ category: string; children: ReactNode }> = ({ category, children }) => {
  const companies = useSampleCompanies();
  // The deal grid joins companies through the app store, not the sample store.
  const seeded = useSeedSampleCompanies(companies);
  const seeds = useMemo(
    () => [
      ...baseSampleSeeds(companies),
      seed(dealBoardConfigState, PREVIEW_DEAL_BOARD_CONFIG),
      seed(allDealsState, PREVIEW_DEALS),
      seed(selectedDealTypeState, PREVIEW_DEAL_TYPE_ID),
      seed(selectedDealCategoryState, categoryItem(category)),
    ],
    [companies, category]
  );
  if (!seeded) {
    return null;
  }
  return (
    <SampleScope key={category} seeds={seeds}>
      {children}
    </SampleScope>
  );
};

/** The analytics pages: the live header over the live charts fed a sample funnel, or the stage-duration grid. */
const DealAnalyticsPreview: FC<{ duration: boolean }> = ({ duration }) => (
  <PipelineScope category={DealBoardDealCategory.CURRENT}>
    <div css={analyticsCss}>
      <DealAnalyticsHeader />
      {duration ? (
        <DealAnalyticsDurationView
          stats={PREVIEW_DEAL_STATS}
          loading={LoadingStatus.success}
          selectedDealType={PREVIEW_DEAL_TYPE_ID}
          gridKey='sample'
        />
      ) : (
        <div css={chartsCss}>
          <OverviewChart dealConversion={PREVIEW_CONVERSION_RATES} />
          <ConversionBarChart dealConversion={PREVIEW_CONVERSION_RATES} />
          <BreakdownChart dealsGrouped={PREVIEW_DEALS_GROUPED} />
        </div>
      )}
    </div>
  </PipelineScope>
);

/** The live board page: header, filters and the board or the category's table. */
const DealsPreview: FC<{ category: string }> = ({ category }) => (
  <PipelineScope category={category}>
    <div css={boardCss}>
      <DealFlowHeaderV2 />
      <FilterOptions />
      {category === DealBoardDealCategory.CURRENT ? <DealBoard /> : <CategoryDealTable />}
    </div>
  </PipelineScope>
);

/** The sample Pipeline screen for the current route, so the frost follows the sub-nav. */
export const PipelineModulePreview: FC = () => {
  const { pathname } = useLocation();
  const screen = pipelinePreviewScreen(pathname);
  const setSelectedCategory = useSetAtom(selectedDealCategoryState);
  const category = screen.kind === 'category' ? screen.category : DealBoardDealCategory.CURRENT;

  // The sidebar's check mark follows this atom, which the live board sets from the URL; frosted, it never mounts.
  useEffect(() => {
    setSelectedCategory(categoryItem(category));
    return () => setSelectedCategory(DefaultDealCategory);
  }, [category, setSelectedCategory]);

  switch (screen.kind) {
    case 'analyticsKpis':
      return <DealAnalyticsPreview duration={false} />;
    case 'analyticsDuration':
      return <DealAnalyticsPreview duration />;
    default:
      return <DealsPreview category={category} />;
  }
};
